import React, { useState, useEffect } from "react";
import { IntlShape, FormattedMessage } from "react-intl";

import {
  FlowProgressHeader,
  StepMatch,
} from "views/components/FlowProgressHeader/FlowProgressHeader";
import { Pricing } from "views/components/Pricing/PricingController";
import { FlowButtonBar } from "views/components/FlowButtonBar/FlowButtonBar";
import { TextInput } from "views/components/TextInput/TextInput";
import { ICompanyDetails, IAddress, AccountantTuple } from "types";
import { getErrorMessageFor } from "tools/utilities/getErrorMessageFor";
import { SelectInput } from "views/components/SelectInput/SelectInput";
import { CheckboxInput } from "views/components/CheckboxInput/CheckboxInput";
import { AddressFinder } from "views/components/AddressFinder/AddressFinder";
import { scrollToError } from "tools/utilities/scrollToError";
import { IrdNumberInput } from "views/components/IrdNumberInput/IrdNumberInput";
import { isValidIrdNumber } from "tools/utilities/validators/irdNumber";
import { Button } from "views/components/Button/Button";
import { isValidPhone } from "tools/utilities/validators/phone";
import { AutoComplete } from "views/components/AutoComplete/AutoComplete";

const NO_ACCESS_PAYROLL = "no payroll access";

interface IProps {
  intl: IntlShape;
  goToNextStep: (data: ICompanyDetails) => void;
  goToPreviousStep: () => void;
  defaultValues?: ICompanyDetails;
  isCharity: boolean;
  industryTypeOptions: string[];
  isAccountant: boolean;
  accountantAccessOptions: string[];
  accountantResults: AccountantTuple[];
  accountantLookup: (val: string) => void;
  accountantIsLoading: boolean;
  accountantError?: string;
}

function accountantNeedsAccess(accountantHasAccessTo: string = ""): boolean {
  return accountantHasAccessTo.toLowerCase() !== NO_ACCESS_PAYROLL;
}

const CompanyDetailsTextBlock = ({
  title,
  text,
}: {
  title: string;
  text: string;
}) => (
  <div className="CompanyDetails-textBlock">
    <h3>{title}</h3>
    <p>{text}</p>
  </div>
);

export const CompanyDetailsView = (props: IProps) => {
  const {
    intl,
    goToNextStep,
    goToPreviousStep,
    defaultValues,
    isCharity,
    industryTypeOptions,
    isAccountant,
    accountantAccessOptions,
    accountantLookup,
    accountantResults,
    accountantIsLoading,
    accountantError,
  } = props;

  const [touchedNext, setTouchedNext] = useState(false);
  const [companyName, setCompanyName] = useState(
    defaultValues?.companyName || ""
  );
  const [tradingName, setTradingName] = useState(
    defaultValues?.tradingName || ""
  );
  const [irdNumber, setIrdNumber] = useState(defaultValues?.irdNumber || "");

  // Business address
  const [businessAddress, setBusinessAddress] = useState<IAddress | undefined>(
    defaultValues?.businessAddress
  );

  // Postal address
  const [postalAddress, setPostalAddress] = useState<IAddress | undefined>(
    defaultValues?.postalAddress
  );

  const [industryType, setIndustryType] = useState(
    defaultValues?.industryType || ""
  );
  const [charityRegistrationNumber, setCharityRegistrationNumber] = useState(
    defaultValues?.charityRegistrationNumber || ""
  );
  const [postalSameAsBusiness, setPostalSameAsBusiness] = useState(
    defaultValues?.postalSameAsBusiness || false
  );

  const [accountant, setAccountant] = useState<AccountantTuple | undefined>(
    defaultValues?.accountant
  );

  const [
    hasSkippedAccountantDetails,
    setHasSkippedAccountantDetails,
  ] = useState(defaultValues?.hasSkippedAccountantDetails || false);
  const [accountantNotListed, setAccountantNotListed] = useState(
    defaultValues?.accountantNotListed || false
  );
  const [accountantBusinessName, setAccountantBusinessName] = useState(
    defaultValues?.accountantBusinessName || ""
  );
  const [accountantPhoneNumber, setAccountantPhoneNumber] = useState(
    defaultValues?.accountantPhoneNumber || ""
  );

  const [accountantHasAccessTo, setAccountantHasAccessTo] = useState(
    defaultValues?.accountantHasAccessTo || ""
  );
  const [accountantName, setAccountantName] = useState(
    defaultValues?.accountantName || ""
  );
  const [
    accountantWillPaySmartPayrollFees,
    setAccountantWillPaySmartPayrollFees,
  ] = useState<boolean>(
    defaultValues?.accountantWillPaySmartPayrollFees || false
  );
  const [
    shouldAddToAccountantsUmbrellaPage,
    setShouldAddToAccountantsUmbrellaPage,
  ] = useState<boolean>(
    defaultValues?.shouldAddToAccountantsUmbrellaPage || false
  );
  const [umbrellaPageName, setUmbrellaPageName] = useState(
    defaultValues?.umbrellaPageName || ""
  );

  const companyNameIsValid = !!companyName;
  const irdNumberIsValid = isValidIrdNumber(irdNumber);
  const industryTypeIsValid = !!industryType;
  const charityRegistrationNumberIsValid =
    !isCharity || (isCharity && !!charityRegistrationNumber);

  const [businessAddressIsValid, setBusinessAddressIsValid] = useState(false);
  const [postalAddressIsValid, setPostalAddressIsValid] = useState(false);

  useEffect(() => {
    if (postalSameAsBusiness) {
      setPostalAddress(undefined);
      setPostalAddressIsValid(true);
    }
  }, [postalSameAsBusiness, setPostalAddressIsValid]);

  const toggleAccountantEntry = () => {
    const accountantNotListedLocal = !accountantNotListed;

    if (accountantNotListedLocal) {
      setAccountant(undefined);
    } else {
      setAccountantBusinessName("");
      setAccountantPhoneNumber("");
    }

    setAccountantNotListed(accountantNotListedLocal);
  };

  // Accountants Details should not be mandatory when a customer is completing the form, only when an accountant is completing it
  const accountantIsValid = !!accountant || accountantNotListed;
  const accountantBusinessNameIsValid =
    !accountantNotListed || !!accountantBusinessName;
  const accountantPhoneNumberIsValid =
    !accountantNotListed || isValidPhone(accountantPhoneNumber);
  const accountantHasAccessToIsValid = !!accountantHasAccessTo;

  const accountantNameIsValid =
    (!isAccountant && accountantNeedsAccess(accountantHasAccessTo) === false) ||
    (accountantNeedsAccess(accountantHasAccessTo) && !!accountantName);

  const isAccountantSectionValid = hasSkippedAccountantDetails === true ||
    isAccountant === true ||
    (accountantIsValid &&
      accountantBusinessNameIsValid &&
      accountantPhoneNumberIsValid &&
      accountantNameIsValid);

  const umbrellaPageNameIsValid =
    !shouldAddToAccountantsUmbrellaPage ||
    (shouldAddToAccountantsUmbrellaPage && !!umbrellaPageName);

  const isValid =
    companyNameIsValid &&
    irdNumberIsValid &&
    industryTypeIsValid &&
    charityRegistrationNumberIsValid &&
    isAccountantSectionValid &&
    umbrellaPageNameIsValid &&
    businessAddressIsValid &&
    postalAddressIsValid;

  const onNext = () => {
    setTouchedNext(true);

    if (!isValid) {
      scrollToError();
      return;
    }

    goToNextStep({
      companyName,
      tradingName,
      irdNumber,
      industryType,
      businessAddress,
      postalAddress,
      charityRegistrationNumber,
      hasSkippedAccountantDetails,
      accountant,
      accountantNotListed,
      accountantBusinessName,
      accountantPhoneNumber,
      accountantHasAccessTo,
      accountantName,
      accountantWillPaySmartPayrollFees,
      shouldAddToAccountantsUmbrellaPage,
      umbrellaPageName,
      postalSameAsBusiness,
    });
  };

  return (
    <div className="CompanyDetails">
      <FlowProgressHeader flowMatch={StepMatch.CompanyDetails} />

      <div className="Container">
        <div className="Grid">
          {/* Left */}
          <div className="Grid-cell u-width1of1 u-md-width1of2 u-lg-width7of12 u-xl-width2of3">
            <div className="Tile">
              <div className="CompanyDetails-container">
                <h3 className="CompanyDetails-title">
                  <FormattedMessage id="COMPANY_DETAILS" />
                </h3>

                <div className="CompanyDetails-block">
                  <TextInput
                    className="TextInput--3of4"
                    id="companyName"
                    value={companyName}
                    onChange={setCompanyName}
                    label={intl.formatMessage({ id: "REGISTERED_COMPANY" })}
                    type="text"
                    placeholder={intl.formatMessage({
                      id: "PLACEHOLDER_REGISTERED_COMPANY",
                    })}
                    error={
                      touchedNext &&
                      getErrorMessageFor(
                        companyNameIsValid,
                        "ERROR_REGISTERED_COMPANY",
                        intl
                      )
                    }
                  />
                  <TextInput
                    className="TextInput--3of4"
                    id="tradingName"
                    value={tradingName}
                    onChange={setTradingName}
                    label={intl.formatMessage({ id: "TRADING_NAME" })}
                    type="text"
                    placeholder={intl.formatMessage({
                      id: "PLACEHOLDER_TRADING_NAME",
                    })}
                  />

                  <IrdNumberInput
                    className="TextInput--1of2"
                    id="irdNumber"
                    value={irdNumber}
                    onChange={setIrdNumber}
                    label={intl.formatMessage({ id: "BUSINESS_IRD" })}
                    type="text"
                    placeholder={intl.formatMessage({ id: "PLACEHOLDER_IRD" })}
                    error={
                      touchedNext &&
                      getErrorMessageFor(
                        irdNumberIsValid,
                        "ERROR_BUSINESS_IRD",
                        intl
                      )
                    }
                  />

                  <SelectInput
                    id="industryType"
                    defaultValue={industryType}
                    onChange={setIndustryType}
                    label={intl.formatMessage({ id: "INDUSTRY_TYPE" })}
                    options={industryTypeOptions}
                    placeholder={intl.formatMessage({ id: "SELECT" })}
                    error={
                      touchedNext &&
                      getErrorMessageFor(
                        industryTypeIsValid,
                        "ERROR_INDUSTRY_TYPE",
                        intl
                      )
                    }
                  />

                  {/* Charity number */}
                  {isCharity && (
                    <div className="CompanyDetails-charity">
                      <TextInput
                        id="charityRegistrationNumber"
                        value={charityRegistrationNumber}
                        onChange={setCharityRegistrationNumber}
                        label={intl.formatMessage({
                          id: "CHARITY_REGISTRATION_NUMBER",
                        })}
                        type="text"
                        placeholder=""
                        error={
                          touchedNext &&
                          getErrorMessageFor(
                            charityRegistrationNumberIsValid,
                            "ERROR_CHARITY_REGISTRATION_NUMBER",
                            intl
                          )
                        }
                      />
                    </div>
                  )}
                </div>

                <div className="CompanyDetails-block">
                  <h3 className="CompanyDetails-title">
                    <FormattedMessage id="BUSINESS_ADDRESS" />
                  </h3>

                  <AddressFinder
                    touchedNext={touchedNext}
                    id="businessAddress"
                    onChange={setBusinessAddress}
                    defaultValues={businessAddress}
                    onValid={setBusinessAddressIsValid}
                    errorMessageIntlId="ERROR_BUSINESS_ADDRESS"
                  />
                </div>
                <div className="CompanyDetails-block">
                  <div className="CompanyDetails-header">
                    <h3 className="CompanyDetails-title">
                      <FormattedMessage id="POSTAL_ADDRESS" />
                    </h3>

                    <CheckboxInput
                      id="postalSameAsBusiness"
                      value="postalSameAsBusiness"
                      isChecked={postalSameAsBusiness}
                      onChange={() =>
                        setPostalSameAsBusiness(!postalSameAsBusiness)
                      }
                      label={intl.formatMessage({ id: "SAME_AS_BUSINESS" })}
                    />
                  </div>

                  {!postalSameAsBusiness && (
                    <AddressFinder
                      touchedNext={touchedNext}
                      id="postalAddress"
                      onChange={setPostalAddress}
                      defaultValues={postalAddress}
                      onValid={setPostalAddressIsValid}
                      errorMessageIntlId="ERROR_POSTAL_ADDRESS"
                      overrideAddressIsValid={postalSameAsBusiness}
                    />
                  )}
                </div>

                <div className="CompanyDetails-block">
                  <h3 className="CompanyDetails-title">
                    <FormattedMessage id="ACCOUNTANT_DETAILS" />
                  </h3>

                  {isAccountant && (
                    <>
                      <CompanyDetailsTextBlock
                        title={intl.formatMessage({
                          id: "SELECT_YOUR_ACCOUNTANT_FIRST_PERSON",
                        })}
                        text={
                          (accountantNotListed
                            ? accountantBusinessName
                            : accountant?.name) || ""
                        }
                      />

                      {accountantNotListed && (
                        <>
                          <CompanyDetailsTextBlock
                            title={intl.formatMessage({
                              id: "ACCOUNTANT_PHONE_NUMBER_FIRST_PERSON",
                            })}
                            text={accountantPhoneNumber}
                          />
                        </>
                      )}
                    </>
                  )}

                  {!isAccountant && (
                    <>
                      <CheckboxInput
                        className="u-mT-md"
                        id="hasSkippedAccountantDetails"
                        value="hasSkippedAccountantDetails"
                        isChecked={hasSkippedAccountantDetails}
                        onChange={() =>
                          setHasSkippedAccountantDetails(
                            !hasSkippedAccountantDetails
                          )
                        }
                        label={intl.formatMessage({
                          id: "I_DO_NOT_HAVE_AN_ACCOUNTANT",
                        })}
                      />

                      {/* Hide remainder of Account Detail section if they don't have this info */}
                      {!hasSkippedAccountantDetails && (
                        <>
                          {!accountantNotListed && (
                            <AutoComplete
                              id="accountant"
                              selectedItem={accountant?.name || ""}
                              onSelect={(val) => {
                                const selectedAccountantTuple = accountantResults.find(
                                  ({ name }) => val === name
                                );
                                setAccountant(selectedAccountantTuple);
                              }}
                              label={intl.formatMessage({
                                id: "SELECT_YOUR_ACCOUNTANT",
                              })}
                              error={
                                touchedNext &&
                                ((!accountantIsValid && accountantError) ||
                                  getErrorMessageFor(
                                    accountantIsValid,
                                    "ERROR_SELECT_YOUR_ACCOUNTANT",
                                    intl
                                  ))
                              }
                              placeholder={intl.formatMessage({
                                id: "START_TYPING_ACCOUNTANT",
                              })}
                              clearLabel={intl.formatMessage({
                                id: "BUTTON_CLEAR",
                              })}
                              noResultsMessage={
                                <div className="AddressFinder-noResults">
                                  <FormattedMessage
                                    tagName="p"
                                    id="NO_RESULTS"
                                  />
                                </div>
                              }
                              isLoading={accountantIsLoading}
                              items={accountantResults
                                .map(({ name }) => name)
                                .slice(0, 15)}
                              onInputValueChange={(val) => {
                                if (val && val.length >= 3) {
                                  accountantLookup(val);
                                }
                              }}
                            />
                          )}

                          <Button
                            label={intl.formatMessage({
                              id: accountantNotListed
                                ? "ACCOUNTANT_HIDE_MANUAL"
                                : "ACCOUNTANT_NOT_LISTED",
                            })}
                            className="CompanyDetails-accountant Button Button-link"
                            buttonType="button"
                            onClick={toggleAccountantEntry}
                          />

                          {/* Only appears if customer selects accountantNotListed */}
                          {accountantNotListed && (
                            <div>
                              <TextInput
                                id="accountantBusinessName"
                                value={accountantBusinessName}
                                onChange={setAccountantBusinessName}
                                type="text"
                                label={intl.formatMessage({
                                  id: "ACCOUNTANT_BUSINESS_NAME",
                                })}
                                error={
                                  touchedNext &&
                                  getErrorMessageFor(
                                    accountantBusinessNameIsValid,
                                    "ERROR_ACCOUNTANT_BUSINESS_NAME",
                                    intl
                                  )
                                }
                                placeholder={intl.formatMessage({
                                  id: "ENTER_ACCOUNTANT_NAME",
                                })}
                              />
                              <TextInput
                                id="accountantPhoneNumber"
                                value={accountantPhoneNumber}
                                onChange={setAccountantPhoneNumber}
                                type="tel"
                                label={intl.formatMessage({
                                  id: "ACCOUNTANT_PHONE_NUMBER",
                                })}
                                error={
                                  touchedNext &&
                                  getErrorMessageFor(
                                    accountantPhoneNumberIsValid,
                                    "ERROR_ACCOUNTANT_PHONE_NUMBER",
                                    intl
                                  )
                                }
                                placeholder={intl.formatMessage({
                                  id: "ENTER_ACCOUNTANT_PHONE_NUMBER",
                                })}
                              />
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}

                  {/* GIVE_ACCOUNTANT_PERMISSION */}
                  {!hasSkippedAccountantDetails && (
                    <SelectInput
                    id="accountantHasAccessTo"
                    defaultValue={accountantHasAccessTo}
                    onChange={setAccountantHasAccessTo}
                    label={intl.formatMessage({
                      id: isAccountant
                        ? "GIVE_ACCOUNTANT_PERMISSION_FIRST_PERSON"
                        : "GIVE_ACCOUNTANT_PERMISSION",
                    })}
                    options={accountantAccessOptions}
                    placeholder={intl.formatMessage({ id: "SELECT" })}
                    error={
                      touchedNext &&
                      getErrorMessageFor(
                        accountantHasAccessToIsValid,
                        isAccountant
                          ? "ERROR_GIVE_ACCOUNTANT_PERMISSION_FIRST_PERSON"
                          : "ERROR_GIVE_ACCOUNTANT_PERMISSION",
                        intl
                      )
                    }
                  />
                  )}
                  

                  {/* Only presented if gives some permission - i.e. any selection other than 'No payroll access' */}
                  {accountantHasAccessTo &&
                    accountantNeedsAccess(accountantHasAccessTo) && (
                      <>
                        {isAccountant && (
                          <CompanyDetailsTextBlock
                            title={intl.formatMessage({
                              id: "ACCOUNTANT_NAME_FIRST_PERSON",
                            })}
                            text={accountantName}
                          />
                        )}
                        {!isAccountant && (
                          <TextInput
                            className="TextInput--3of4"
                            id="accountantName"
                            value={accountantName}
                            onChange={setAccountantName}
                            type="text"
                            label={intl.formatMessage({
                              id: "ACCOUNTANT_NAME",
                            })}
                            error={
                              touchedNext &&
                              getErrorMessageFor(
                                accountantNameIsValid,
                                "ERROR_ACCOUNTANT_NAME",
                                intl
                              )
                            }
                            placeholder={intl.formatMessage({
                              id: "ENTER_ACCOUNTANT_NAME",
                            })}
                          />
                        )}
                      </>
                    )}

                  {isAccountant && (
                    <div className="CompanyDetails-isAccountant">
                      {/* Only show if customer has checked they are an accountant completing on behalf of the customer on SIG01-03 */}
                      <CheckboxInput
                        id="accountantWillPaySmartPayrollFees"
                        value="accountantWillPaySmartPayrollFees"
                        isChecked={accountantWillPaySmartPayrollFees}
                        onChange={() =>
                          setAccountantWillPaySmartPayrollFees(
                            !accountantWillPaySmartPayrollFees
                          )
                        }
                        label={intl.formatMessage({
                          id: "ACCOUNTANT_TO_PAY_ON_BEHALF",
                        })}
                      />
                      <CheckboxInput
                        id="shouldAddToAccountantsUmbrellaPage"
                        value="shouldAddToAccountantsUmbrellaPage"
                        isChecked={shouldAddToAccountantsUmbrellaPage}
                        onChange={() =>
                          setShouldAddToAccountantsUmbrellaPage(
                            !shouldAddToAccountantsUmbrellaPage
                          )
                        }
                        label={intl.formatMessage({
                          id: "ADD_CUSTOMER_TO_MY_UMBRELLA",
                        })}
                      />

                      {/* Only show if customer has checked the box that states they want the customer added to their umbrella account  */}
                      {shouldAddToAccountantsUmbrellaPage && (
                        <TextInput
                          id="umbrellaPageName"
                          value={umbrellaPageName}
                          onChange={setUmbrellaPageName}
                          type="text"
                          label={intl.formatMessage({
                            id: "UMBRELLA_PAGE_USERNAME",
                          })}
                          error={
                            touchedNext &&
                            getErrorMessageFor(
                              umbrellaPageNameIsValid,
                              "ERROR_UMBRELLA_PAGE_USERNAME",
                              intl
                            )
                          }
                          placeholder={intl.formatMessage({
                            id: "ENTER_UMBRELLA_USERNAME",
                          })}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>

              <FlowButtonBar
                secondaryLabel={intl.formatMessage({ id: "BUTTON_BACK" })}
                onPrimary={onNext}
                onSecondary={goToPreviousStep}
              />
            </div>
          </div>

          {/* Right */}
          <div className="Grid-cell u-width1of1 u-md-width1of2 u-lg-width5of12 u-xl-width1of3">
            <Pricing />
          </div>
        </div>
      </div>
    </div>
  );
};
